import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../app/StoreContext';
import { useApp } from '../../app/AppContext';
import { callFn } from '../../lib/api';
import { money, pct, resumesLabel } from '../../lib/types';
import type { Fulfillment, PaymentIntentStart, Quote } from '../../lib/types';
import { PaymentSheet } from '../../components/PaymentSheet';
import { loadContact, saveContact } from '../../lib/contact';
import type { Contact } from '../../lib/contact';

export function Cart() {
  const { slug, store, cart, setCartQuantity, removeFromCart, clearCart, boundHere, boundElsewhere } = useStore();
  const { me, authed, isGuest, account, ready, toast } = useApp();
  const nav = useNavigate();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [fulfillment, setFulfillment] = useState<Fulfillment>('pickup');
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState<'store' | 'online' | null>(null);
  const [intent, setIntent] = useState<PaymentIntentStart | null>(null);
  // Contact details (PLAN_GUEST_CHECKOUT): name + email always, phone for
  // pickup/delivery. Prefilled from the account for members, from the last
  // order on this browser for guests.
  const [contact, setContact] = useState<Contact>(() => loadContact());
  const [contactTouched, setContactTouched] = useState(false);

  useEffect(() => {
    if (contactTouched || !account) return;
    setContact((c) => ({
      name: c.name || [account.givenName, account.familyName].filter(Boolean).join(' '),
      email: c.email || (account.email.endsWith('@guests.nexus.invalid') ? '' : account.email),
      phone: c.phone || account.phone || '',
    }));
  }, [account, contactTouched]);

  const canOrder = authed && boundHere;
  const key = JSON.stringify(cart);

  // Server-side quote (client prices are never trusted).
  useEffect(() => {
    if (!canOrder || cart.length === 0) { setQuote(null); setQuoteError(null); return; }
    let live = true;
    const t = setTimeout(() => {
      callFn<Quote>('quote_order', {
        items: cart, fulfillment,
        ...(fulfillment === 'delivery' && address.trim() ? { deliveryAddress: address.trim() } : {}),
      })
        .then((q) => { if (live) { setQuote(q); setQuoteError(null); } })
        .catch((e) => { if (live) { setQuote(null); setQuoteError(e instanceof Error ? e.message : 'Could not price your cart.'); } });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [key, canOrder, cart, fulfillment, address]);

  // A store can turn delivery off while this tab is open. Never leave the
  // customer on a method the store will refuse.
  const deliveryOffered = store?.fulfillment?.delivery?.offered !== false;
  const deliveryPaused = store?.fulfillment?.delivery?.paused === true;
  const deliveryOpen = deliveryOffered && !deliveryPaused;
  // Quote first (it reflects this cart), store settings as the fallback before
  // the first quote comes back.
  const deliveryFee = quote?.delivery?.feeCents ?? store?.fulfillment?.delivery?.feeCents ?? 0;
  const deliveryEta = quote?.delivery?.etaMinutes ?? store?.fulfillment?.delivery?.etaMinutes ?? null;
  const deliveryArea = store?.fulfillment?.delivery?.zips ?? [];
  useEffect(() => {
    if (!deliveryOpen && fulfillment === 'delivery') setFulfillment('pickup');
  }, [deliveryOpen, fulfillment]);

  const trimmedContact = (): Contact => ({ name: contact.name.trim(), email: contact.email.trim(), phone: contact.phone.trim() });
  const orderPayload = () => ({
    items: cart, channel: 'cart', fulfillment,
    ...(fulfillment === 'delivery' ? { deliveryAddress: address.trim() } : {}),
    contact: trimmedContact(),
  });

  const validate = (): boolean => {
    const c = trimmedContact();
    if (!c.name) { toast('Please enter your name.', 'err'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) { toast('Please enter a valid email address.', 'err'); return false; }
    if (c.phone.replace(/\D/g, '').length < 7) { toast('Please enter a phone number so the store can reach you.', 'err'); return false; }
    if (fulfillment === 'delivery' && !address.trim()) { toast('Please enter a delivery address.', 'err'); return false; }
    if (fulfillment === 'delivery' && quote?.delivery && !quote.delivery.available) {
      toast(quote.delivery.message || 'Delivery is not available for this order.', 'err'); return false;
    }
    saveContact(c);
    return true;
  };
  const afterOrder = (orderId: string) => {
    clearCart();
    nav(`/${slug}/orders`, { state: { highlight: orderId, offerAccount: isGuest, contact: trimmedContact() } });
  };

  const payAtStore = async () => {
    if (!validate()) return;
    setBusy('store');
    try {
      const r = await callFn<{ orderId: string }>('place_order', { ...orderPayload(), paymentMethod: 'pay_at_store' });
      toast('Order placed — pay when you pick up.', 'ok');
      afterOrder(r.orderId);
    } catch (e) { toast(e instanceof Error ? e.message : 'Could not place the order.', 'err'); }
    finally { setBusy(null); }
  };

  const payOnline = async () => {
    if (!validate()) return;
    setBusy('online');
    try {
      const r = await callFn<PaymentIntentStart>('create_payment_intent', orderPayload());
      setIntent(r);
    } catch (e) { toast(e instanceof Error ? e.message : 'Could not start payment.', 'err'); }
    finally { setBusy(null); }
  };

  const onPaid = async () => {
    if (!intent) return;
    try {
      await callFn('confirm_payment', { orderId: intent.orderId });
      setIntent(null);
      toast('Payment received — order placed.', 'ok');
      afterOrder(intent.orderId);
    } catch (e) { toast(e instanceof Error ? e.message : 'Payment could not be confirmed.', 'err'); }
  };

  const onCancelPay = async () => {
    if (!intent) return;
    const id = intent.orderId;
    setIntent(null);
    try { await callFn('abandon_unpaid_order', { orderId: id }); } catch { /* best effort */ }
  };

  if (!store) return null;

  if (cart.length === 0) {
    return (
      <div className="page-narrow center">
        <h1>Your cart is empty</h1>
        <Link className="btn" to={`/${slug}`}>Browse the menu</Link>
      </div>
    );
  }

  const canPayOnline = !!quote?.payments?.configured && !!quote?.payments?.publishableKey && (quote?.totalCents ?? 0) >= 50;
  // The server refuses this combination; do not let the customer discover that
  // by pressing the button.
  const blocked = (fulfillment === 'delivery' && !!quote?.delivery && !quote.delivery.available)
    || quote?.ordering?.open === false;

  return (
    <div className="page-narrow cart">
      <h1>Your cart</h1>
      <ul className="cart-lines">
        {cart.map((l) => {
          const priced = quote?.lines.find((q) => q.item.toLowerCase() === l.item.toLowerCase() && (q.notes || '') === (l.notes || ''));
          return (
            <li key={`${l.item}|${l.notes || ''}`} className="cart-line">
              <div className="cart-line-text">
                <span className="item-name">{l.item}</span>
                {l.notes && <span className="muted small">{l.notes}</span>}
                {priced && <span className="muted small">{priced.price} each</span>}
              </div>
              <div className="qty">
                <button className="btn btn-sm btn-ghost" aria-label={`Fewer ${l.item}`} onClick={() => setCartQuantity(l.item, l.quantity - 1, l.notes || '')}>−</button>
                <span>{l.quantity}</span>
                <button className="btn btn-sm btn-ghost" aria-label={`More ${l.item}`} onClick={() => setCartQuantity(l.item, l.quantity + 1, l.notes || '')}>+</button>
              </div>
              <span className="price">{priced ? money(priced.lineCents) : ''}</span>
              <button className="btn btn-sm btn-ghost" aria-label={`Remove ${l.item}`} onClick={() => removeFromCart(l.item, l.notes || '')}>✕</button>
            </li>
          );
        })}
      </ul>

      {!authed && ready && (
        <div className="callout"><p className="muted">Starting your session…</p></div>
      )}
      {authed && boundElsewhere && (
        <div className="callout">
          <p>Your account belongs to {me?.displayName}. Sign in with a {store.displayName} account to order here.</p>
          <Link className="btn btn-sm" to={`/${slug}/login`} state={{ returnTo: `/${slug}/cart` }}>Sign in</Link>
        </div>
      )}
      {authed && !boundHere && !boundElsewhere && (
        <div className="callout"><p>Your account is not set up for ordering yet.</p></div>
      )}

      {canOrder && (
        <>
          <fieldset className="contact">
            <legend>Your details</legend>
            <div className="grid-2">
              <label>Name<input className="input" autoComplete="name" required maxLength={120} value={contact.name} onChange={(e) => { setContactTouched(true); setContact({ ...contact, name: e.target.value }); }} /></label>
              <label>Phone<input className="input" type="tel" autoComplete="tel" inputMode="tel" required maxLength={32} value={contact.phone} onChange={(e) => { setContactTouched(true); setContact({ ...contact, phone: e.target.value }); }} /></label>
            </div>
            <label>Email<input className="input" type="email" autoComplete="email" required value={contact.email} onChange={(e) => { setContactTouched(true); setContact({ ...contact, email: e.target.value }); }} /></label>
            <p className="muted small">We'll email your order confirmation here.{isGuest && <> Have an account? <Link to={`/${slug}/login`} state={{ returnTo: `/${slug}/cart` }}>Sign in</Link> to fill this in for you.</>}</p>
          </fieldset>

          <fieldset className="fulfillment">
            <legend>How would you like it?</legend>
            <label><input type="radio" name="f" checked={fulfillment === 'pickup'} onChange={() => setFulfillment('pickup')} /> Pickup</label>
            {deliveryOffered && (
              <label>
                <input type="radio" name="f" disabled={!deliveryOpen} checked={fulfillment === 'delivery'} onChange={() => setFulfillment('delivery')} />
                {' '}Delivery{deliveryFee > 0 && !deliveryPaused ? ` (${money(deliveryFee)})` : ''}
              </label>
            )}
            {deliveryOffered && deliveryPaused && (
              <p className="muted small">
                {store.fulfillment.delivery.note || 'Delivery is paused right now.'}
                {resumesLabel(store.fulfillment.delivery.resumesAt) && ` It comes ${resumesLabel(store.fulfillment.delivery.resumesAt)}.`}
              </p>
            )}
            {fulfillment === 'delivery' && (
              <>
                <input className="input" placeholder="Delivery address, including ZIP code" value={address} maxLength={300} onChange={(e) => setAddress(e.target.value)} />
                {deliveryArea.length > 0 && <p className="muted small">We deliver to {deliveryArea.join(', ')}.</p>}
                {deliveryEta && <p className="muted small">About {deliveryEta} minutes.</p>}
                {quote?.delivery && !quote.delivery.available && <p className="error">{quote.delivery.message}</p>}
              </>
            )}
          </fieldset>

          {quote?.ordering?.open === false && (
            <div className="callout">
              <p>{quote.ordering.message}</p>
              {resumesLabel(quote.ordering.resumesAt) && (
                <p className="muted small">Ordering resumes on its own — {resumesLabel(quote.ordering.resumesAt)}.</p>
              )}
            </div>
          )}
          {quoteError && <p className="error">{quoteError}</p>}
          {quote && (
            <dl className="totals">
              <div><dt>Subtotal</dt><dd>{money(quote.subtotalCents)}</dd></div>
              {quote.taxRateBps > 0 && <div><dt>Tax ({pct(quote.taxRateBps)})</dt><dd>{money(quote.taxCents)}</dd></div>}
              {quote.deliveryFeeCents > 0 && <div><dt>Delivery</dt><dd>{money(quote.deliveryFeeCents)}</dd></div>}
              <div className="total"><dt>Total</dt><dd>{money(quote.totalCents)}</dd></div>
            </dl>
          )}

          <div className="row wrap" style={{ gap: 12 }}>
            <button className="btn" disabled={!quote || !!busy || blocked} onClick={() => void payAtStore()}>{busy === 'store' ? 'Placing…' : 'Pay at store'}</button>
            {canPayOnline && (
              <button className="btn btn-secondary" disabled={!quote || !!busy || blocked} onClick={() => void payOnline()}>{busy === 'online' ? 'Starting…' : 'Pay now'}</button>
            )}
          </div>
        </>
      )}

      {intent && (
        <PaymentSheet
          intent={intent}
          returnUrl={`${window.location.origin}/${slug}/orders?confirm=${encodeURIComponent(intent.orderId)}`}
          primaryColor={store.branding.colors.primary}
          onPaid={onPaid}
          onCancel={onCancelPay}
        />
      )}
    </div>
  );
}
